/* eslint-disable upright/no-primer-link */
import { Box, Button, Link as PrimerLink } from '@primer/react';
import { isValidInternalURL, isValidURL } from '@upright/common/utils/input-parsers';
import { Link as ReactRouterLink } from 'react-router-dom';
import styled from 'styled-components';
import { TextSize } from '../text/types';
import {
  commonLinkStyles,
  iconWrapStyles,
  linkButtonLikeStyles,
  linkWithIconStyles,
  linkWithoutIconStyles,
} from './styles';
import { FontWeight, LinkIndicator, LinkPlatformProps } from './types';

/* DIRECTIONS:
 * Use links to navigate the user to a new place or content. Do not use links for activating features – that's a job for buttons.
 * Read more about accessible links: https://primer.style/design/guides/accessibility/links

1. Always provide an indicator – it's a required prop since links should be indicated either with color (we use blue), underlining, an icon, or
   for some cases button-like styles.
   * If you choose buttonLike, you can also provide the sizeIfButtonLike prop. That will also determine the font size so do not provide fontSize separately. Also, do not provide an icon in this case.
   * For rare cases where the context makes it obvious that this is a link, you can pass the value OVERRIDE to proceed with an unstyled link.

2. Provide an internal link with the "to" prop and an external link with "href". The console will shout if you won't.

3. If you provide an icon, use the optional props leadingIcon or trailingIcon to do it.
   * By default, the font weight is semibold for a link with an icon. If you must, you can override this.

4. By default, the link's font size is inherited. But you can also specify a font size from one of the four TextSize options.
*/

export const Link = (props: LinkPlatformProps) => {
  const {
    to,
    href,
    leadingIcon: LeadingIcon,
    trailingIcon: TrailingIcon,
    indicator,
    fontSize,
    fontWeight,
    onClick,
    sizeIfButtonLike,
    style,
    isDisabled,
    ...rest
  } = props;

  // Providing "to" prop indicates an internal link and renders a React Router Link instead of Primer's Link component

  if (to) {
    if (!isValidInternalURL(to)) console.error(`Invalid internal link: ${to}`);
  } else if (href) {
    if (!isValidURL(href)) console.error(`Invalid external link: ${href}`);
  } else {
    console.error('No link provided');
  }

  if (indicator === 'buttonLike') {
    const ButtonLinkContent = (
      <Button
        as="span"
        variant="invisible"
        size={sizeIfButtonLike}
        sx={{ border: '1px solid', borderColor: 'border.default' }}
      >
        {props.children}
      </Button>
    );

    if (isDisabled) {
      return <>{ButtonLinkContent}</>;
    }
    if (to) {
      return (
        <StyledRouterLinkThatLooksLikeButton to={to} style={style}>
          {ButtonLinkContent}
        </StyledRouterLinkThatLooksLikeButton>
      );
    }
    return <StyledPrimerButtonLikeLink href={href}>{ButtonLinkContent}</StyledPrimerButtonLikeLink>;
  }

  if (LeadingIcon || TrailingIcon) {
    const IconLinkContent = (
      <>
        {LeadingIcon && (
          <Box as="span" data-component="leadingVisual" sx={{ ...iconWrapStyles }}>
            <LeadingIcon />
            {props.children}
          </Box>
        )}
        {TrailingIcon && (
          <Box as="span" data-component="trailingVisual" sx={{ ...iconWrapStyles }}>
            {props.children}
            <TrailingIcon />
          </Box>
        )}
      </>
    );

    if (isDisabled) {
      return <>{IconLinkContent}</>;
    }

    if (to) {
      return (
        <StyledRouterLinkWithIcon to={to} fontSize={fontSize} fontWeight={fontWeight} onClick={onClick} style={style}>
          {IconLinkContent}
        </StyledRouterLinkWithIcon>
      );
    }
    return (
      <StyledPrimerLinkWithIcon href={href} fontSize={fontSize} fontWeight={fontWeight} onClick={onClick} style={style}>
        {IconLinkContent}
      </StyledPrimerLinkWithIcon>
    );
  }

  if (isDisabled) {
    return <>{props.children}</>;
  }

  if (to) {
    return (
      <StyledRouterLink
        to={to}
        indicator={indicator}
        fontSize={fontSize}
        fontWeight={fontWeight}
        onClick={onClick}
        style={style}
        {...rest}
      >
        {props.children}
      </StyledRouterLink>
    );
  }
  return (
    <StyledPrimerLink
      href={href}
      indicator={indicator}
      fontSize={fontSize}
      fontWeight={fontWeight}
      onClick={onClick}
      {...rest}
    >
      {props.children}
    </StyledPrimerLink>
  );
};

const BasePrimerLink = styled(PrimerLink)`
  ${commonLinkStyles};
`;

const StyledPrimerLink = styled(BasePrimerLink)<{
  indicator: LinkIndicator;
  fontSize?: TextSize;
  fontWeight?: FontWeight;
}>`
  ${linkWithoutIconStyles};
`;

const StyledPrimerLinkWithIcon = styled(BasePrimerLink)`
  ${linkWithIconStyles};
`;

const StyledPrimerButtonLikeLink = styled(PrimerLink)`
  ${linkButtonLikeStyles};
  &:hover {
    text-decoration: none;
  }
`;

const BaseRouterLink = styled(ReactRouterLink)`
  ${commonLinkStyles};
`;

const StyledRouterLink = styled(BaseRouterLink)<{
  indicator: LinkIndicator;
  fontSize?: TextSize;
  fontWeight?: FontWeight;
}>`
  ${linkWithoutIconStyles};
`;

const StyledRouterLinkWithIcon = styled(BaseRouterLink)`
  ${linkWithIconStyles};
`;

const StyledRouterLinkThatLooksLikeButton = styled(ReactRouterLink)`
  ${linkButtonLikeStyles}
`;
